import { useMemo, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { sendPasswordResetEmail } from 'firebase/auth';
import { read as readWorkbook, utils as sheetUtils } from 'xlsx';
import {
  MAX_IMPORT_ROWS,
  importCommitResponseSchema,
  importValidateResponseSchema,
  type ImportCommitResponse,
  type ImportRow,
  type ImportValidateResponse,
  type UserDetail,
} from '@unit/shared';
import { auth } from '../../lib/firebase.js';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ActionError } from '../../components/ActionError.js';
import {
  IMPORT_FIELDS,
  REQUIRED_FIELDS,
  autoMap,
  missingRequired,
  toImportRows,
  type Mapping,
} from './importMapping.js';

const primary =
  'rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40';
const secondary =
  'rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100 disabled:opacity-40';

interface Parsed {
  fileName: string;
  headers: string[];
  cells: unknown[][];
}

type Invites = { sent: number; failed: string[] };

/**
 * Bulk import (REQUIREMENTS §2.7): upload → map columns → preview with a
 * per-row error report → commit → invites.
 *
 * The spreadsheet never leaves the browser as a file. It is parsed here, and
 * only mapped rows reach the server — which validates them again itself and
 * refuses the whole batch on any error. The preview shows exactly what the
 * server will say, because it is the server saying it.
 */
/** Example files an admin can fill in; served as-is from `apps/web/public/import`. */
const EXAMPLE_FILES = [
  { href: '/import/soldiers-example.xlsx', label: 'import.exampleXlsx' },
  { href: '/import/soldiers-example-he.csv', label: 'import.exampleCsvHe' },
  { href: '/import/soldiers-example-en.csv', label: 'import.exampleCsvEn' },
] as const;

export function ImportPage() {
  const { t } = useTranslation();
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [report, setReport] = useState<ImportValidateResponse | null>(null);
  const [committed, setCommitted] = useState<ImportCommitResponse | null>(null);
  const [invites, setInvites] = useState<Invites | null>(null);
  // Owner 2026-09-13: a bulk import must not email anyone unless asked.
  // Everyone in the file can sign in by email link regardless.
  const [sendEmails, setSendEmails] = useState(false);

  const validate = useAction((rows: ImportRow[]) =>
    apiPost('/users/import/validate', importValidateResponseSchema, { rows }),
  );
  const commit = useAction((rows: ImportRow[]) =>
    apiPost('/users/import/commit', importCommitResponseSchema, { rows }),
  );

  const lines = useMemo(
    () => (parsed && mapping ? toImportRows(parsed.cells, mapping) : []),
    [parsed, mapping],
  );
  const missing = mapping ? missingRequired(mapping) : REQUIRED_FIELDS.slice();

  const onFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setParseError(null);
    setReport(null);
    setCommitted(null);
    setInvites(null);
    try {
      const workbook = readWorkbook(await file.arrayBuffer(), { type: 'array' });
      const first = workbook.SheetNames[0];
      const sheet = first ? workbook.Sheets[first] : undefined;
      if (!sheet) throw new Error('empty');
      const grid = sheetUtils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });
      const [headerRow, ...cells] = grid;
      if (!headerRow || headerRow.length === 0) throw new Error('empty');
      const headers = headerRow.map((h) => String(h ?? ''));
      setParsed({ fileName: file.name, headers, cells });
      setMapping(autoMap(headers));
    } catch {
      setParsed(null);
      setMapping(null);
      setParseError(t('import.parseFailed'));
    }
  };

  const runValidate = async () => {
    setCommitted(null);
    setInvites(null);
    const result = await validate.run(lines.map((l) => l.row));
    if (result) setReport(result);
  };

  const runCommit = async () => {
    const result = await commit.run(lines.map((l) => l.row));
    if (!result) return;
    setCommitted(result);
    if (result.report) {
      // The file changed under us, or someone took a number meanwhile. The
      // report is the same shape the preview shows; show it there.
      setReport(result.report);
      return;
    }
    if (sendEmails && result.created.length > 0) {
      setInvites(await sendInvites(result.created));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <h2 className="text-lg font-semibold text-slate-900">{t('import.title')}</h2>
        <p className="mt-1 text-sm text-slate-500">{t('import.intro', { max: MAX_IMPORT_ROWS })}</p>
        {/* Static files under apps/web/public/import — the columns are documented in docs/import.md. */}
        <p className="mt-2 text-sm text-slate-600" data-testid="import-examples">
          {t('import.examples')}{' '}
          {EXAMPLE_FILES.map((file, i) => (
            <span key={file.href}>
              {i > 0 && ' · '}
              <a href={file.href} download className="underline decoration-slate-400 hover:text-slate-900">
                {t(file.label)}
              </a>
            </span>
          ))}
        </p>

        <label className="mt-4 block text-sm text-slate-700">
          {t('import.chooseFile')}
          <input
            type="file"
            accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => void onFile(e)}
            data-testid="import-file"
            className="mt-1 block w-full text-sm text-slate-600 file:me-3 file:rounded-lg file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white"
          />
        </label>
        {parseError && (
          <p role="alert" className="mt-2 text-sm text-red-600">
            {parseError}
          </p>
        )}
      </section>

      {parsed && mapping && (
        <section
          className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200"
          data-testid="import-mapping"
        >
          <h3 className="font-semibold text-slate-900">{t('import.mapTitle')}</h3>
          <p className="mt-1 text-sm text-slate-500">
            {t('import.mapHint', { file: parsed.fileName, rows: lines.length })}
          </p>

          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {IMPORT_FIELDS.map((field) => (
              <label key={field} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-700">
                  {t(`import.fields.${field}`)}
                  {REQUIRED_FIELDS.includes(field) && <span className="text-red-600"> *</span>}
                </span>
                <select
                  value={mapping[field] ?? ''}
                  onChange={(e) =>
                    setMapping({
                      ...mapping,
                      [field]: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  data-testid={`map-${field}`}
                  className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
                >
                  <option value="">{t('import.unmapped')}</option>
                  {parsed.headers.map((h, i) => (
                    <option key={i} value={i}>
                      {h || t('import.column', { n: i + 1 })}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>

          {missing.length > 0 && (
            <p className="mt-3 text-sm text-amber-800" data-testid="import-missing">
              {t('import.missingRequired', {
                fields: missing.map((f) => t(`import.fields.${f}`)).join(', '),
              })}
            </p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void runValidate()}
              disabled={validate.busy || missing.length > 0 || lines.length === 0}
              data-testid="import-validate"
              className={primary}
            >
              {validate.busy ? t('import.validating') : t('import.validate')}
            </button>
          </div>
          <div className="mt-3">
            <ActionError error={validate.error} />
          </div>
        </section>
      )}

      {report && parsed && (
        <section
          className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200"
          data-testid="import-report"
        >
          <h3 className="font-semibold text-slate-900">{t('import.previewTitle')}</h3>
          <p
            className={`mt-1 text-sm ${report.invalid > 0 ? 'text-amber-800' : 'text-emerald-800'}`}
            data-testid="import-summary"
          >
            {report.invalid > 0
              ? t('import.summaryInvalid', { valid: report.valid, invalid: report.invalid })
              : t('import.summaryValid', { valid: report.valid })}
          </p>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs text-slate-500">
                  <th className="py-2 text-start font-medium">{t('import.line')}</th>
                  <th className="py-2 text-start font-medium">{t('org.columns.name')}</th>
                  <th className="py-2 text-start font-medium">{t('org.columns.personalNumber')}</th>
                  <th className="py-2 text-start font-medium">{t('users.email')}</th>
                  <th className="py-2 text-start font-medium">{t('import.unit')}</th>
                  <th className="py-2 text-start font-medium">{t('import.problems')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {report.rows.map((r) => {
                  const line = lines[r.index];
                  if (!line) return null;
                  const bad = r.issues.length > 0;
                  return (
                    <tr
                      key={r.index}
                      className={bad ? 'bg-red-50' : ''}
                      data-testid={`import-row-${r.index}`}
                      data-ok={bad ? 'false' : 'true'}
                    >
                      <td className="py-1.5 text-slate-500">
                        <span className="latin-inline">{line.line}</span>
                      </td>
                      <td className="py-1.5 text-slate-900">
                        {line.row.firstName} {line.row.lastName}
                      </td>
                      <td className="py-1.5 text-slate-600">
                        <span className="latin-inline">{line.row.personalNumber}</span>
                      </td>
                      <td className="py-1.5 text-slate-600">
                        <span className="latin-inline">{line.row.email}</span>
                      </td>
                      <td className="py-1.5 text-slate-600">
                        {line.row.platoon} · {line.row.team}
                      </td>
                      <td className="py-1.5 text-red-700">
                        {r.issues
                          .map((i) => t(`import.issues.${i.code}`))
                          .join('; ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void runCommit()}
              disabled={commit.busy || report.invalid > 0 || (committed?.created.length ?? 0) > 0}
              data-testid="import-commit"
              className={primary}
            >
              {commit.busy ? t('import.committing') : t('import.commit', { count: report.valid })}
            </button>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={sendEmails}
                onChange={(e) => setSendEmails(e.target.checked)}
                disabled={commit.busy || (committed?.created.length ?? 0) > 0}
                data-testid="import-send-emails"
              />
              {t('import.sendEmails')}
            </label>
            <button type="button" onClick={() => void runValidate()} className={secondary}>
              {t('import.revalidate')}
            </button>
          </div>
          <div className="mt-3">
            <ActionError error={commit.error} />
            {committed?.rolledBack && (
              <p role="alert" className="text-sm text-red-600" data-testid="import-rolled-back">
                {t('import.rolledBack')}
              </p>
            )}
          </div>
        </section>
      )}

      {committed && committed.created.length > 0 && (
        <section
          className="rounded-2xl bg-emerald-50 p-5 ring-1 ring-emerald-200"
          data-testid="import-done"
        >
          <h3 className="font-semibold text-emerald-900">
            {t('import.done', { count: committed.created.length })}
          </h3>
          {invites && (
            <p className="mt-1 text-sm text-emerald-900" data-testid="import-invites">
              {t('import.invites', { sent: invites.sent, failed: invites.failed.length })}
            </p>
          )}
          {invites && invites.failed.length > 0 && (
            <ul className="mt-2 text-sm text-amber-900">
              {invites.failed.map((email) => (
                <li key={email} className="latin-inline">
                  {email}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

/**
 * One invite per created account, sequentially: Firebase throttles bursts,
 * and a failed invite is not a failed import — the account exists and the
 * invite can be resent from the soldier's page.
 */
async function sendInvites(created: UserDetail[]): Promise<Invites> {
  const result: Invites = { sent: 0, failed: [] };
  for (const user of created) {
    if (!user.email) continue;
    try {
      await sendPasswordResetEmail(auth, user.email);
      result.sent += 1;
    } catch {
      result.failed.push(user.email);
    }
  }
  return result;
}
